import React from 'react';
import { useRouteError, isRouteErrorResponse, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';
import { useDirection } from '../contexts/DirectionContext';
import { useTheme } from '../contexts/ThemeContext';
import { ErrorCategory } from '../utils/errorMapping';
import { ErrorCode } from '../types/errors';
import SEO from '../components/common/SEO';
import { ROUTES } from '../router/routes';
// import { cn } from '../utils/styles';

interface ErrorVisualConfig {
  icon: string;
  bgColor: string;
  iconColor: string;
  statusCode?: string;
}

// Move error visual configs to a separate constant
const ERROR_VISUAL_CONFIG: Record<ErrorCategory, ErrorVisualConfig> = {
  [ErrorCategory.ROUTE_NOT_FOUND]: {
    icon: 'ph:warning-circle',
    bgColor: 'bg-primary-500/10',
    iconColor: 'text-primary-500',
    statusCode: '404',
  },
  [ErrorCategory.RESOURCE_NOT_FOUND]: {
    icon: 'ph:file-x',
    bgColor: 'bg-secondary-500/10',
    iconColor: 'text-secondary-500',
    statusCode: '404',
  },
  [ErrorCategory.NETWORK_ERROR]: {
    icon: 'ph:wifi-slash',
    bgColor: 'bg-accent-500/10',
    iconColor: 'text-accent-500',
  },
  [ErrorCategory.AUTHENTICATION_ERROR]: {
    icon: 'ph:lock-key',
    bgColor: 'bg-secondary-500/10',
    iconColor: 'text-secondary-500',
    statusCode: '401',
  },
  [ErrorCategory.SERVER_ERROR]: {
    icon: 'ph:warning-octagon',
    bgColor: 'bg-status-error/10',
    iconColor: 'text-status-error',
    statusCode: '500',
  },
  [ErrorCategory.VALIDATION_ERROR]: {
    icon: 'ph:info',
    bgColor: 'bg-status-warning/10',
    iconColor: 'text-status-warning',
  },
  [ErrorCategory.UNKNOWN]: {
    icon: 'ph:info',
    bgColor: 'bg-status-warning/10',
    iconColor: 'text-status-warning',
  },
} as const;

interface ErrorDetails {
  title: string;
  message: string;
  code: ErrorCode;
  status?: number;
}

const DEFAULT_ERROR: ErrorDetails = {
  title: 'errors.somethingWentWrong',
  message: 'errors.tryAgainLater',
  code: ErrorCode.UNKNOWN,
  status: 500
};

const NOT_FOUND_ERROR: ErrorDetails = {
  title: 'errors.pageNotFound',
  message: 'errors.pageNotFound',
  code: ErrorCode.ROUTE_NOT_FOUND,
  status: 404
};

const ErrorPage: React.FC = () => {
  const error = useRouteError();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isRTL } = useDirection();
  const { theme } = useTheme();
  const isDark = theme.mode === 'dark';

  const getErrorDetails = (): ErrorDetails => {
    // Handle 404s from catch-all route
    if (location.pathname !== '/') {
      return NOT_FOUND_ERROR;
    }

    // Handle route errors
    if (isRouteErrorResponse(error)) {
      return error.status === 404
        ? NOT_FOUND_ERROR
        : { ...DEFAULT_ERROR, status: error.status };
    }

    // Handle thrown error objects
    if (error instanceof Error) {
      const errorWithStatus = error as Error & { status?: number; code?: ErrorCode };
      return {
        title: errorWithStatus.message || DEFAULT_ERROR.title,
        message: 'errors.tryAgainLater',
        code: errorWithStatus.code || DEFAULT_ERROR.code,
        status: errorWithStatus.status || DEFAULT_ERROR.status
      };
    }

    return DEFAULT_ERROR;
  };

  const errorDetails = getErrorDetails();

  // Determine error category based on error code
  let errorCategory: ErrorCategory;
  switch (errorDetails.code) {
    case ErrorCode.ROUTE_NOT_FOUND:
      errorCategory = ErrorCategory.ROUTE_NOT_FOUND;
      break;
    case ErrorCode.RESOURCE_NOT_FOUND:
      errorCategory = ErrorCategory.RESOURCE_NOT_FOUND;
      break;
    case ErrorCode.UNAUTHORIZED:
    case ErrorCode.FORBIDDEN:
    case ErrorCode.AUTH_INVALID:
    case ErrorCode.AUTH_EXPIRED:
      errorCategory = ErrorCategory.AUTHENTICATION_ERROR;
      break;
    case ErrorCode.VALIDATION:
    case ErrorCode.CONTENT_INVALID:
      errorCategory = ErrorCategory.VALIDATION_ERROR;
      break;
    case ErrorCode.SERVER_ERROR:
    case ErrorCode.EXTERNAL_SERVICE_ERROR:
    case ErrorCode.EXTERNAL_SERVICE_TIMEOUT:
      errorCategory = ErrorCategory.SERVER_ERROR;
      break;
    case ErrorCode.NETWORK_ERROR:
      errorCategory = ErrorCategory.NETWORK_ERROR;
      break;
    default:
      errorCategory = ErrorCategory.UNKNOWN;
  }

  const visualConfig = ERROR_VISUAL_CONFIG[errorCategory];

  // Get SEO title based on error type
  const getSEOTitle = () => {
    if (errorDetails.status === 404) {
      return t('errors.seo.notFoundTitle');
    }
    return t('errors.seo.errorTitle', { code: errorDetails.status });
  };

  // Get SEO description based on error type
  const getSEODescription = () => {
    if (errorDetails.status === 404) {
      return t('errors.seo.notFoundDescription');
    }
    return t('errors.seo.defaultDescription');
  };

  return (
    <div className={`
      h-[calc(100vh)] flex relative overflow-hidden
      bg-background-primary
      text-text-primary
      ${isRTL ? 'rtl' : 'ltr'}
    `}>
      <SEO
        title={getSEOTitle()}
        description={getSEODescription()}
        keywords={[
          'Error page',
          errorDetails.status === 404 ? 'Page not found' : 'Error',
          'Help',
          'Support'
        ]}
        noindex={true}
      />

      {/* Background Pattern */}
      <div className="absolute inset-0">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, var(--colors-border-primary) 1px, transparent 0)`,
          backgroundSize: '40px 40px',
          opacity: isDark ? 0.1 : 0.08
        }} />
      </div>

      {/* Animated Gradient */}
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute -inset-[40%] animate-slow-spin blur-3xl ${isDark ? 'opacity-20 mix-blend-lighten' : 'opacity-30'}`}
          style={{
            background: isDark
              ? `conic-gradient(from 0deg at 50% 50%,
                  var(--colors-${visualConfig.iconColor.split('-')[1]}-400) 0deg,
                  var(--colors-${visualConfig.iconColor.split('-')[1]}-500) 120deg,
                  var(--colors-${visualConfig.iconColor.split('-')[1]}-600) 240deg,
                  var(--colors-${visualConfig.iconColor.split('-')[1]}-400) 360deg
                )`
              : `conic-gradient(from 0deg at 50% 50%,
                  var(--colors-${visualConfig.iconColor.split('-')[1]}-600) 0deg,
                  var(--colors-accent-500) 100deg,
                  var(--colors-${visualConfig.iconColor.split('-')[1]}-600) 180deg,
                  var(--colors-${visualConfig.iconColor.split('-')[1]}-900) 360deg
                )`
          }}
        />
      </div>

      {/* Main Content */}
      <div className="w-full flex items-center">
        <div className={`w-full max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 py-12 sm:py-16 ${isRTL ? 'direction-rtl' : 'direction-ltr'}`}>
          {/* Error Code Badge */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-8 flex justify-center"
          >
            <span className={`
              px-4 py-2 inline-flex items-center gap-2 text-lg font-medium tracking-wider uppercase animate-fade-in
              ${isDark 
                ? 'bg-[rgba(var(--colors-background-primary-rgb),0.5)] text-text-secondary' 
                : 'bg-[rgba(var(--colors-primary-300-rgb),0.1)] text-background-primary shadow-sm'
              }`}
            >
              <Icon 
                icon={visualConfig.icon} 
                className={`w-5 h-5 ${visualConfig.iconColor} inline-block`} 
              />
              <span className={`inline-block ${visualConfig.iconColor}`}>{visualConfig.statusCode || t('errors.error')}</span>
            </span>
          </motion.div>

          {/* Error Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className={`mt-8 text-4xl font-bold tracking-tight sm:text-6xl animate-slide-up 
              ${isDark ? 'text-text-primary' : 'text-secondary-700'}`}
          >
            {t(errorDetails.title)}
          </motion.h1>
          
          {/* Error Message */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className={`mt-6 text-lg leading-8 animate-slide-up animation-delay-200 font-thin
              ${isDark ? 'text-text-secondary' : 'text-secondary-700 opacity-60'}`}
          >
            {t(errorDetails.message)}
          </motion.p>

          {/* Action Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mt-10 animate-slide-up animation-delay-400"
          >
            <Link
              to={ROUTES.HOME}
              className={`group inline-flex items-center gap-2 text-base font-medium transition-all duration-300 hover:gap-3
                ${isDark 
                  ? 'text-primary-400 hover:text-primary-300' 
                  : 'text-primary-600 hover:text-primary-700'
                }`}
            >
              <Icon 
                icon={isRTL ? "ph:arrow-right-thin" : "ph:arrow-left-thin"} 
                className={`w-5 h-5 transition-transform ${isRTL ? 'rtl:ml-1' : 'ltr:mr-1'} group-hover:translate-x-${isRTL ? '1' : '-1'}`}
              />
              {t('common.backToHome')}
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ErrorPage;